import { DebugService } from '../shared/services/debug.service.js';
import { stratum } from '../../constants/stratum.js';
import { REFI_PERSONAL_CONST } from '../shared/constants/app.constants.js';
import { Application } from '../components/model/application.model.js';

class _ApplicationService {
  constructor() {
    this.debug = new DebugService('ApplicationService');
    this._application = new Application();
    this._customerOffer = {};
    this._matchingState = '';
    this.primaryEmailChanged = false;
    this.coAppEmailChanged = false;

    // Determining if DOB or SSN have changed since prefill value
    this.primaryDobChanged = false;
    this.coAppDobChanged = false;

    this.primarySsnChanged = false;
    this.coAppSsnChanged = false;

    this._isUpfrontOffers = false;
    this._isIconicAndToggledOn = false;
    this._isIconicV2 = false;
    this._hidePrimarySSN = false;
    this._experiments = [];
    this.abTest = null;
    // test name, weight and group are defaulted and should not be used/modified.
    this._abTests = {
      name: 'personal-ab-tests',
      variationIds: [],
      testGroup: '',
      testWeight: 1,
    };
  }

  set Application(application) {
    this._application = application;
    this.updateSessionStore(application);
  }

  get Application() {
    if (this._isV2Routing && !sessionStorage.getItem('refi-prequal-store')) {
      this.setApplication(new Application());
      return this._application;
    }
    if (this._isV2Routing && sessionStorage.getItem('refi-prequal-store')) {
      this.debug.log(
        'Getting application from session storage',
        JSON.parse(sessionStorage.getItem('refi-prequal-store'))?.state
      );
      return (
        JSON.parse(sessionStorage.getItem('refi-prequal-store'))?.state ??
        this._application
      );
    }
    return this._application;
  }

  updateSessionStore(application) {
    if (this._isV2Routing) {
      this.debug.log('Setting application in session storage to', application);
      // Set refi-prequal-store session object
      const sessionStore = { state: application };
      sessionStorage.setItem(
        'refi-prequal-store',
        JSON.stringify(sessionStore)
      );

      document.dispatchEvent(
        new CustomEvent('update-prequal-store', {
          bubbles: true,
          composed: true,
        })
      );
      return;
    }

    this._application = application;
  }

  setApplication(application) {
    this._application = application;
    this.updateSessionStore(application);
  }

  get CustomerOffer() {
    return this._customerOffer;
  }

  set CustomerOffer(customerOffer) {
    this._customerOffer = customerOffer;
  }

  get experiments() {
    return this._experiments;
  }

  get hidePrimarySSN() {
    return this._hidePrimarySSN;
  }

  set hidePrimarySSN(hidePrimarySSN) {
    this._hidePrimarySSN = hidePrimarySSN;
  }

  get isIconicAndToggledOn() {
    return this._isIconicAndToggledOn;
  }

  set isIconicAndToggledOn(isIconicAndToggledOn) {
    this._isIconicAndToggledOn = isIconicAndToggledOn;
  }

  get isIconicV2() {
    return this._isIconicV2;
  }

  set isIconicV2(isIconicV2) {
    this._isIconicV2 = isIconicV2;
  }

  updateIconicV2() {
    this.debug.log(
      'updating iconic v2 toggle, this.experiments is ',
      this._experiments
    );
    this._isIconicV2 = this._experiments?.includes(
      REFI_PERSONAL_CONST.ICONIC_V2
    );
  }

  updateExperiments(
    newExperiments,
    removeExperiments = [],
    removeExperimentType = null
  ) {
    stratum.endAbTest(this.abTest);
    const newlyAddedExperiments = newExperiments.map((experiment) =>
      experiment.toLowerCase()
    );
    let updatedExperimentsNoSpecificType = this._experiments;
    if (removeExperimentType) {
      updatedExperimentsNoSpecificType = this._experiments.filter(
        (experiment) =>
          !experiment.toLowerCase().includes(removeExperimentType.toLowerCase())
      );
    }
    const updatedExperiments = updatedExperimentsNoSpecificType.filter(
      (experiment) => !removeExperiments.includes(experiment.toLowerCase())
    );
    this._experiments = Array.from(
      new Set(updatedExperiments.concat(newlyAddedExperiments))
    );
    this._abTests.variationIds = this._experiments;
    this.abTest = stratum.startAbTest(this._abTests);
  }

  get MatchingState() {
    return this._matchingState;
  }

  set MatchingState(matchingState) {
    this._matchingState = matchingState;
  }

  get getPrimaryApplicantEmailChanged() {
    return this.primaryEmailChanged;
  }

  set primaryApplicantEmailChanged(value) {
    this.primaryEmailChanged = value;
  }

  get getCoApplicantEmailChanged() {
    return this.coAppEmailChanged;
  }

  set coApplicantEmailChanged(value) {
    this.coAppEmailChanged = value;
  }

  get getPrimaryApplicantDobChanged() {
    return this.primaryDobChanged;
  }

  set primaryApplicantDobChanged(value) {
    this.primaryDobChanged = value;
  }

  get getCoApplicantDobChanged() {
    return this.coAppDobChanged;
  }

  set coApplicantDobChanged(value) {
    this.coAppDobChanged = value;
  }

  get getPrimaryApplicantSsnChanged() {
    return this.primarySsnChanged;
  }

  set primaryApplicantSsnChanged(value) {
    this.primarySsnChanged = value;
  }

  get getCoApplicantSsnChanged() {
    return this.coAppSsnChanged;
  }

  set coApplicantSsnChanged(value) {
    this.coAppSsnChanged = value;
  }

  get isUpfrontOffers() {
    return this._isUpfrontOffers;
  }

  set isUpfrontOffers(isUpfrontOffers) {
    this._isUpfrontOffers = isUpfrontOffers;
  }

  set isV2Routing(v2Routing) {
    this._isV2Routing = v2Routing;
  }

  get isV2Routing() {
    return this._isV2Routing;
  }

  clearCoapplicant() {
    this._application.coApplicant = JSON.parse(
      JSON.stringify({
        lastName: '',
        firstName: '',
        emailAddress: '',
        confirmEmailAddress: '',
        phoneNumber: '',
        phoneType: 'mobile',
        smsOptIn: false,
        currentAddress: {
          street: '',
          aptNumber: null,
          city: '',
          stateCode: '',
          zipCode: '',
        },
        currentAddressType: 'other',
        ssn: '',
        dateOfBirth: '',
        employmentStatus: '',
        employmentInfo: {
          employerName: '',
          yearsEmployed: null,
          monthsEmployed: null,
          jobDescription: '',
          activeIncomeVerification: null,
        },
        incomeAmount: null,
        otherIncome: null,
        profileReferenceId: '',
        prospectId: '',
        externalId: '',
        clientCorrelationId: '',
        isSameAddress: null,
        incomeFrequency: '',
        otherIncomeFrequency: '',
      })
    );
    this.updateSessionStore(this._application);
  }

  setMockApplication() {
    this.debug.log('Setting mock application');
    this._application = {
      loanType: 'REFINANCE',
      applicationType: 'INDIVIDUAL',
      profileType: 'financial',
      primaryApplicant: {
        lastName: 'John',
        firstName: 'Doe',
        emailAddress: 'John.Doe@capitalone.com',
        lastFourTaxId: '1234',
        confirmEmailAddress: '',
        phoneNumber: '616-568-2912',
        phoneType: 'mobile',
        smsOptIn: false,
        currentAddress: {
          street: '113 Pine Meadows',
          aptNumber: '',
          city: 'Dallas',
          stateCode: 'FL',
          zipCode: '75230',
          ownerStatus: 'OWN',
          monthlyPayment: 1000,
        },
        currentAddressType: 'other',
        ssn: '',
        dateOfBirth: '06/11/1978',
        employmentStatus: 'employed',
        employmentInfo: {
          employerName: 'Capital One',
          yearsEmployed: null,
          monthsEmployed: null,
          jobDescription: 'Software Engineer',
          activeIncomeVerification: null,
        },
        incomeAmount: 80000,
        otherIncome: null,
        prospectId: '',
        clientCorrelationId: '',
        isSameAddress: null,
      },
      coApplicant: {
        lastName: '',
        firstName: '',
        emailAddress: '',
        lastFourTaxId: '',
        confirmEmailAddress: '',
        phoneNumber: '',
        phoneType: 'mobile',
        smsOptIn: false,
        currentAddress: {
          street: '',
          aptNumber: null,
          city: '',
          stateCode: '',
          zipCode: '',
        },
        currentAddressType: 'other',
        ssn: '',
        dateOfBirth: '01/01/2000',
        employmentStatus: '',
        employmentInfo: {
          employerName: '',
          yearsEmployed: null,
          monthsEmployed: null,
          jobDescription: '',
          activeIncome: '',
          activeIncomeVerification: null,
        },
        incomeAmount: null,
        otherIncome: null,
        profileReferenceId: '',
        prospectId: '',
        externalId: '',
        clientCorrelationId: '',
        isSameAddress: null,
      },
      promoCode: '11491700',
      agreeToTerms: null,
      agreeToLookup: null,
      year: '',
      make: '',
      model: '',
      trim: '',
      vin: '',
      wholeSaleValue: '',
      paymentDueDay: null,
      manualEntryFlag: null,
      selectedLoan: null,
    };
    this.updateSessionStore(this._application);
  }
}

export const ApplicationService = new _ApplicationService();
